import { Cabin } from '../components/Cabin';
import { Dialogue } from '../components/Dialogue';
import { Flame } from '../components/Flame';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { Triangle } from '../components/Triangle';
import { THEMES } from '../lib/theme';

export type LandingMode = 'login' | 'signup';

type Props = {
  onEnter: (mode: LandingMode) => void;
};

export function LandingScreen({ onEnter }: Props) {
  return (
    <div className="landing-stage">
      <header className="landing-ribbon">
        <div className="landing-ribbon__brand">
          <Cabin size={20} />
          <span>HABIT HOLLOW</span>
        </div>
        <span className="landing-ribbon__spacer" />
        <PixelButton variant="ghost" onClick={() => onEnter('login')}>
          LOG IN
        </PixelButton>
        <PixelButton variant="gold" onClick={() => onEnter('signup')}>
          <Triangle /> BEGIN
        </PixelButton>
      </header>

      <section className="landing-hero">
        <div className="landing-hero__mark" aria-hidden="true">
          <Cabin size={84} />
        </div>
        <h1 className="landing-hero__title">HABIT HOLLOW</h1>
        <div className="landing-hero__sub">
          <span>a cosy little chore book by the hearth</span>
        </div>
      </section>

      <Panel title="◇ WELCOME, TRAVELLER ◇" style={{ marginBottom: 28 }}>
        <Dialogue speaker="MAYOR" style={{ marginBottom: 22 }}>
          Welcome to the Hollow! It's a quiet little valley where folks
          keep a small book by the hearth — a page of daily habits and a
          scrap of errands for the week. Won't you settle in and start a
          page of your own?
        </Dialogue>
        <div className="landing-cta-row">
          <PixelButton variant="gold" onClick={() => onEnter('signup')}>
            <Triangle /> BEGIN YOUR LEDGER
          </PixelButton>
          <PixelButton variant="ghost" onClick={() => onEnter('login')}>
            Already a neighbour? LOG IN
          </PixelButton>
        </div>
      </Panel>

      <div className="landing-section-label">
        <span>— WHAT YOU'LL FIND IN TOWN —</span>
      </div>

      <div className="landing-grid">
        <Panel
          title="◆ THE QUEST BOARD"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art">
            <div className="landing-quest-row">
              <span className="pixel-check done" aria-hidden="true" />
              <span className="landing-quest-row__text landing-quest-row__text--done">
                tend the herb garden
              </span>
            </div>
            <div className="landing-quest-row">
              <span className="pixel-check done" aria-hidden="true" />
              <span className="landing-quest-row__text landing-quest-row__text--done">
                bake a loaf of bread
              </span>
            </div>
            <div className="landing-quest-row">
              <span className="pixel-check" aria-hidden="true" />
              <span className="landing-quest-row__text">
                write a short letter
              </span>
            </div>
          </div>
          <div className="landing-feature__copy">
            A daily page of small chores — tick them off as you go. Habits
            you mean to keep, errands you mean to run.
          </div>
        </Panel>

        <Panel
          title="◆ STREAK FLAMES"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art landing-feature__art--center">
            <div className="landing-flames">
              <Flame />
              <Flame />
              <Flame />
              <Flame />
              <Flame />
              <span className="landing-flames__count">7 DAYS</span>
            </div>
          </div>
          <div className="landing-feature__copy">
            Every habit you tend lights a little flame. Feed it day by day
            and watch the streak grow warmer at the hearth.
          </div>
        </Panel>

        <Panel
          title="◆ SIX TRADES"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art">
            <div className="landing-trades">
              <span className="tag tag--moss">HEALTH</span>
              <span className="tag tag--gold">CRAFT</span>
              <span className="tag tag--plum">MIND</span>
              <span className="tag tag--brick">HOME</span>
              <span className="tag tag--moss">KIN</span>
              <span className="tag tag--gold">WORK</span>
            </div>
          </div>
          <div className="landing-feature__copy">
            Sort your chores by what they tend to — health, craft, mind,
            home, kin, or work. Each trade has its own warm colour.
          </div>
        </Panel>

        <Panel
          title="◆ MONTHLY LEDGER"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art landing-feature__art--center">
            <MiniCalendar />
          </div>
          <div className="landing-feature__copy">
            A calendar view of your month — the warmer a square glows, the
            fuller the day. A quiet satisfaction at a glance.
          </div>
        </Panel>

        <Panel
          title="◆ PRIVATE JOURNAL"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art landing-feature__art--center">
            <PixelLock />
          </div>
          <div className="landing-feature__copy">
            Your quest log is just for you. Folded away behind a little
            door only your key can open.
          </div>
        </Panel>

        <Panel
          title="◆ SIX COSY THEMES"
          bodyClassName="panel-body landing-feature"
        >
          <div className="landing-feature__art landing-feature__art--center">
            <div className="landing-themes">
              {THEMES.map(t => (
                <span
                  key={t.k}
                  className="landing-theme-chip"
                  style={{ background: t.dot }}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
          <div className="landing-feature__copy">
            Pick the light that suits your morning — candlelit hearth,
            autumn dusk, harvest morn, and more.
          </div>
        </Panel>
      </div>

      <Panel title="◇ SETTLING IN ◇" style={{ marginTop: 28 }}>
        <Dialogue speaker="MAYOR" style={{ marginBottom: 22 }}>
          Found a cosy little spot? Pull up a chair and we'll get you in
          the ledger — no fuss. Just a name and a key, and your page is
          yours alone, tucked safe by the hearth.
        </Dialogue>
        <div className="landing-cta-row">
          <PixelButton variant="gold" onClick={() => onEnter('signup')}>
            <Triangle /> BEGIN YOUR LEDGER
          </PixelButton>
          <PixelButton variant="ghost" onClick={() => onEnter('login')}>
            LOG IN
          </PixelButton>
        </div>
      </Panel>

      <div className="landing-foot">
        ◆ habit hollow · a small 16-bit habit log · v0.1 ◆
      </div>
    </div>
  );
}

/** 4×7 mini month grid — decorative. Warmer cells = fuller days. */
function MiniCalendar() {
  // 0 = empty, 1 = light, 2 = medium, 3 = full, 4 = today (gold)
  const pattern = [
    0, 0, 1, 2, 1, 0, 0,
    1, 2, 3, 3, 2, 1, 0,
    2, 3, 3, 3, 2, 1, 1,
    3, 3, 4, 0, 0, 0, 0,
  ];
  return (
    <div className="landing-cal" aria-hidden="true">
      {pattern.map((v, i) => (
        <span key={i} className={`landing-cal__cell landing-cal__cell--${v}`} />
      ))}
    </div>
  );
}

/** Pixel padlock — a cosy stand-in for "your journal is private". */
function PixelLock() {
  return (
    <svg
      aria-hidden="true"
      width={64}
      height={78}
      viewBox="0 0 9 11"
      shapeRendering="crispEdges"
      className="landing-lock"
    >
      {/* shackle */}
      <rect x="2" y="0" width="5" height="1" fill="currentColor" />
      <rect x="1" y="1" width="1" height="3" fill="currentColor" />
      <rect x="7" y="1" width="1" height="3" fill="currentColor" />
      {/* body */}
      <rect x="0" y="4" width="9" height="7" fill="currentColor" />
      {/* keyhole — painted in parchment so it reads as a cut-out */}
      <rect x="4" y="6" width="1" height="3" className="landing-lock__hole" />
      <rect x="3" y="6" width="3" height="1" className="landing-lock__hole" />
    </svg>
  );
}
